import { jsPDF } from "jspdf";

// Courrier de relance au format PDF, mis en page à partir du texte déjà
// interpolé du modèle (le même que celui du mail). Signé par le cabinet :
// texte seul, pas d'image de signature (décision du client, 01/10/2026).
//
// Structure attendue du modèle : cabinet, destinataire, "Au Tampon, le ...",
// "Objet : ...", "LRAR", paragraphes, puis le bloc signataire. Si le texte a
// été modifié et ne suit plus cette structure, chaque bloc reste affiché en
// paragraphe simple : rien n'est jamais perdu.

const BORDEAUX = [110, 26, 54];
const ENCRE = [31, 22, 32];
const GRIS = [138, 119, 128];

function construireCourrier({ corps }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const L = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const marge = 24;
  const largeur = L - 2 * marge;
  const interligne = 5.4;

  // Blocs séparés par une ligne vide
  const blocs = (corps || "")
    .replace(/\r/g, "")
    .split(/\n\s*\n/)
    .map((b) => b.split("\n").map((l) => l.trim()).filter(Boolean))
    .filter((b) => b.length);

  const cabinet = blocs[0] && blocs[0].length === 1 ? blocs.shift()[0] : "";
  const destinataire = blocs[0] && blocs[0].length <= 3 && !/^(au |objet|lrar)/i.test(blocs[0][0]) ? blocs.shift() : [];
  let signature = [];
  if (blocs.length > 2 && blocs[blocs.length - 1].length >= 2 && blocs[blocs.length - 1].every((l) => l.length < 60)) {
    signature = blocs.pop();
  }

  // En-tête : nom du cabinet et filet bordeaux
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(...BORDEAUX);
  doc.text((cabinet || "ALFA Comptabilité").toUpperCase(), marge, 24);
  doc.setDrawColor(...BORDEAUX);
  doc.setLineWidth(0.6);
  doc.line(marge, 28, L - marge, 28);

  let y = 44;
  const saut = (h) => {
    if (y + h > H - 24) {
      doc.addPage();
      y = 28;
    }
  };

  // Destinataire, aligné à droite
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...ENCRE);
  destinataire.forEach((l, i) => {
    if (i > 0) doc.setFont("helvetica", "normal");
    doc.text(l, L - marge, y, { align: "right" });
    y += interligne;
  });
  y += 8;

  const ecrire = (texte, opts = {}) => {
    const { gras = false, droite = false, justifie = false, couleur = ENCRE, apres = 4 } = opts;
    doc.setFont("helvetica", gras ? "bold" : "normal");
    doc.setFontSize(11);
    doc.setTextColor(...couleur);
    const lignes = doc.splitTextToSize(texte, largeur);
    lignes.forEach((l, i) => {
      saut(interligne);
      if (droite) doc.text(l, L - marge, y, { align: "right" });
      else if (justifie && i < lignes.length - 1 && lignes.length > 1) doc.text(l, marge, y, { align: "justify", maxWidth: largeur });
      else doc.text(l, marge, y);
      y += interligne;
    });
    y += apres;
  };

  blocs.forEach((bloc) => {
    const texte = bloc.join(" ");
    if (/^au .+, le /i.test(texte)) ecrire(texte, { droite: true, apres: 8 });
    else if (/^objet\s*:/i.test(texte)) ecrire(texte, { gras: true, apres: 3 });
    else if (/^lrar$/i.test(texte)) ecrire("Lettre recommandée avec accusé de réception", { gras: true, couleur: BORDEAUX, apres: 8 });
    else if (/^monsieur, madame,?$/i.test(texte)) ecrire(texte, { apres: 5 });
    else ecrire(texte, { justifie: true, apres: 5 });
  });

  // Signature
  if (signature.length) {
    saut(interligne * (signature.length + 2));
    y += 6;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...ENCRE);
    doc.text(signature[0], marge, y);
    y += interligne;
    doc.setFont("helvetica", "normal");
    signature.slice(1).forEach((l) => {
      doc.text(l, marge, y);
      y += interligne;
    });
  }

  // Pied de page sur chaque page
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    doc.setDrawColor(236, 229, 226);
    doc.setLineWidth(0.3);
    doc.line(marge, H - 16, L - marge, H - 16);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...GRIS);
    doc.text(cabinet || "ALFA Comptabilité", marge, H - 11);
    if (total > 1) doc.text(`Page ${p} / ${total}`, L - marge, H - 11, { align: "right" });
  }
  return doc;
}

export const nomFichierCourrier = (niveau, nom) => {
  const slug = String(nom || "client")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
  return `Relance-${niveau}-${slug || "client"}.pdf`;
};

export function genererCourrierPdf({ niveau, nom, corps }) {
  construireCourrier({ corps }).save(nomFichierCourrier(niveau, nom));
}

// Même PDF, en base64, pour la joindre au mail envoyé depuis MyALFA.
export function courrierPdfBase64({ corps }) {
  return construireCourrier({ corps }).output("datauristring").split(",")[1];
}
